import React, { useEffect, useMemo, useRef, useState } from "react";

export default function ArtistCategoryMultiSelect({
  categories,
  options,
  onChange,
  testId,
  allowCustom = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customValue, setCustomValue] = useState("");
  const rootRef = useRef(null);
  const selected = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);
  const availableOptions = useMemo(
    () => [...new Set([...(options || []), ...selected])],
    [options, selected],
  );
  const filteredOptions = availableOptions.filter((category) =>
    category.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const toggleCategory = (category) => {
    onChange(selected.includes(category)
      ? selected.filter((item) => item !== category)
      : [...selected, category]);
  };

  const addCustomCategory = () => {
    const category = customValue.trim();
    if (category && !selected.some((item) => item.toLocaleLowerCase() === category.toLocaleLowerCase())) {
      onChange([...selected, category]);
    }
    setCustomValue("");
    setSearch("");
  };

  return (
    <div className="artist-category-select" ref={rootRef} data-testid={testId}>
      <button
        type="button"
        className="field-input artist-category-select-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
      >
        <span>{selected.length ? `${selected.length} categor${selected.length === 1 ? "y" : "ies"} selected` : "Select artist categories…"}</span>
        <span aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>

      {selected.length > 0 && (
        <div className="artist-category-select-values" aria-label="Selected artist categories">
          {selected.map((category) => (
            <span className="artist-category-select-value" key={category}>
              {category}
              <button
                type="button"
                aria-label={`Remove ${category}`}
                onClick={() => toggleCategory(category)}
                data-testid={`${testId}-remove-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="artist-category-select-menu">
          <input
            type="search"
            className="field-input artist-category-select-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search categories"
            aria-label="Search artist categories"
            data-testid={`${testId}-search`}
          />
          <div className="artist-category-select-options" role="listbox" aria-multiselectable="true">
            {filteredOptions.map((category) => {
              const isSelected = selected.includes(category);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`artist-category-select-option${isSelected ? " selected" : ""}`}
                  key={category}
                  onClick={() => toggleCategory(category)}
                  data-testid={`${testId}-option-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="artist-category-select-checkbox" aria-hidden="true">{isSelected ? "✓" : ""}</span>
                  <span>{category}</span>
                </button>
              );
            })}
            {!filteredOptions.length && <div className="artist-category-select-empty">No matching categories</div>}
          </div>
          {allowCustom && (
            <form
              className="artist-category-select-custom"
              onSubmit={(event) => {
                event.preventDefault();
                addCustomCategory();
              }}
            >
              <input
                type="text"
                className="field-input"
                value={customValue}
                onChange={(event) => setCustomValue(event.target.value)}
                placeholder="Add another category"
                aria-label="Add another category"
                data-testid={`${testId}-custom-input`}
              />
              <button type="submit" className="btn btn-ghost btn-sm" disabled={!customValue.trim()}>Add</button>
            </form>
          )}
          <button type="button" className="artist-category-select-done" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}
